import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  CatalogItem,
  LoginReason,
  Resource,
  Role,
  Tool,
  ToolConfig,
  User,
} from "../../../app/backendTypes";
import {
  useAppSelector,
  useToolBoardingStatus,
  useCanEmployeeLogin,
  useTools,
  useCatalogItems,
  useCatalogBoardingStatus,
} from "../../../app/hooks";
import Error from "../Error";
import ErrorChip from "../ErrorChip";
import ExpandMore from "../ExpandMore";
import Loading from "../Loading";
import BoardingAction from "./BoardingAction";
import JiraProjectsAction from "./Jira/JiraProjectsAction";
import PendingAction from "./PendingAction";
import Avatar from "../Avatar";
import BlackDuckProjectsAction from "./BlackDuck/BlackDuckProjectsAction";
import TaskLoader from "../tasks/TaskLoader";
import PolarisGroupsAction from "./Polaris/PolarisGroupsAction";
import TableauSiteRoleAction from "./Tableau/TableauSiteRoleAction";
import CatalogBoardingAction from "../../catalog/CatalogBoardingAction";

function ToolsOverview(props: { user: User }) {
  const { user } = props;
  const ECONOMIC_LEGAL_UNIT_ID = 110;
  const [expanded, setExpaned] = useState(false);
  const userLegalUnitId = user.employee.workingForLegalUnit?.id;
  const { tools, loading, error } = useTools();
  const { catalogItems } = useCatalogItems();
  const [filteredTools, setFilteredTools] = useState(tools);
  const [filteredCatalogItems, setFilteredCatalogItems] =
    useState(catalogItems);

  useEffect(() => {
    const CatalogItemFiltering = (
      approvedCompaniesIds: number[] | undefined,
      restrictedCompaniesIds: number[] | undefined
    ) => {
      if (userLegalUnitId === null || userLegalUnitId === undefined)
        return true;

      if (
        (approvedCompaniesIds === undefined ||
          approvedCompaniesIds.length === 0) &&
        (restrictedCompaniesIds === undefined ||
          restrictedCompaniesIds.length === 0)
      )
        return true;

      if (
        approvedCompaniesIds !== undefined &&
        restrictedCompaniesIds !== undefined
      )
        return (
          approvedCompaniesIds.includes(userLegalUnitId) &&
          !restrictedCompaniesIds.includes(userLegalUnitId)
        );

      if (
        restrictedCompaniesIds !== undefined &&
        restrictedCompaniesIds.includes(userLegalUnitId)
      )
        return false;

      if (
        approvedCompaniesIds !== undefined &&
        approvedCompaniesIds.includes(userLegalUnitId)
      )
        return true;
      return false;
    };

    const EconomicToolFiltering = (
      legalUnitId: number | undefined,
      tool: Tool
    ) => {
      if (legalUnitId === null) {
        return true;
      }

      if (legalUnitId !== ECONOMIC_LEGAL_UNIT_ID) {
        return true;
      }

      const allowedTools = [
        Tool.BlackDuck,
        Tool.Jira,
        Tool.Confluence,
        Tool.Mural,
        Tool.Polaris,
        Tool.Tableau,
        Tool.Lucid,
        Tool.Udemy,
      ];

      return allowedTools.includes(tool);
    };

    setFilteredTools(
      tools.filter((t) => EconomicToolFiltering(userLegalUnitId, t.kind))
    );

    setFilteredCatalogItems(
      catalogItems.filter((c) =>
        CatalogItemFiltering(
          c.company_permissions?.approved_ids,
          c.company_permissions?.restricted_ids
        )
      )
    );
  }, [catalogItems, tools, userLegalUnitId]);
  let content = <></>;
  if (loading) content = <Loading />;
  else if (error) content = <Error error={error} />;
  else
    content = (
      <ToolsList
        list={filteredTools}
        user={user}
        catalogItems={filteredCatalogItems}
      />
    );

  return (
    <Card sx={{ width: "100%", maxWidth: "sm" }}>
      <CardHeader
        title="Tools"
        subheader="Overview"
        action={
          <ExpandMore expand={expanded} onClick={() => setExpaned(!expanded)}>
            <ExpandMoreIcon />
          </ExpandMore>
        }
      />
      <Collapse in={expanded} timeout="auto">
        <CardContent>{content}</CardContent>
      </Collapse>
    </Card>
  );
}

export default ToolsOverview;

function ToolsList(props: {
  list: ToolConfig[];
  user: User;
  catalogItems: CatalogItem[];
}) {
  const { list, user, catalogItems } = props;

  const isAdmin = useAppSelector((state) =>
    state.auth.roles.includes(Role.Administrator)
  );

  let canOnboard = true;

  const { canLogin, loading, error } = useCanEmployeeLogin(
    user.employee.id,
    false
  );

  if (
    canLogin === null ||
    (canLogin.kind === "No"
      ? // TODO: remove the reason check when we support NDAs for external consultants
      canLogin.inner.reason === LoginReason.ExternalConsultant
        ? !isAdmin
        : true
      : false) ||
    loading ||
    error
  )
    canOnboard = false;

  return (
    <List>
      {list.map((tool, i) => (
        <ListItem
          key={i}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar alt={tool.kind} src={tool.item.tool?.avatar} />
            <Typography>{tool.item.tool?.display_name || tool.kind}</Typography>
          </Box>
          <UserTool user={user} tool={tool.kind} canOnboard={canOnboard} />
        </ListItem>
      ))}

      {catalogItems
        .filter(
          (item) =>
            item.mim_group_id !== undefined && item.mim_group_id !== null
        )
        .map((item, i) => (
          <ListItem
            key={i}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar alt={item.info.display_name} src={item.info.avatar} />
              <Typography>{item.info.display_name}</Typography>
            </Box>
            <UserCatalogItem
              user={user}
              catalogItem={item}
              canOnboard={canOnboard}
            />
          </ListItem>
        ))}
    </List>
  );
}

function UserTool(props: { user: User; tool: Tool; canOnboard: boolean }) {
  const { user, tool, canOnboard } = props;

  const { status, loading, error, reload } = useToolBoardingStatus(
    tool,
    user.employee.id
  );

  let element = <></>;

  if (loading) element = <CircularProgress color="secondary" size={32} />;
  else if (error) element = <ErrorChip error={error} />;
  else if (status !== null)
    element = (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        {tool === Tool.Jira && status.value && (
          <JiraProjectsAction user={user} />
        )}

        {tool === Tool.BlackDuck && status.value && (
          <BlackDuckProjectsAction user={user} />
        )}

        {tool === Tool.Polaris && status.value && (
          <PolarisGroupsAction user={user} />
        )}

        {tool === Tool.Tableau && status.value && (
          <>
            {/* <TableauProjectsAction user={user} /> */}
            <TableauSiteRoleAction user={user} />
          </>
        )}

        {status.task_id !== undefined && status.task_id !== null && (
          <TaskLoader
            id={status.task_id}
            render={(task) => (
              <PendingAction
                task={task}
                callback={reload}
                onCompleted={reload}
              />
            )}
          />
        )}

        <BoardingAction
          disabled={Boolean(status.task_id) || (!status.value && !canOnboard)}
          onboarded={status.value}
          tool={tool}
          user={user}
          callback={reload}
        />
      </Box>
    );

  return element;
}

function UserCatalogItem(props: {
  user: User;
  catalogItem: CatalogItem;
  canOnboard: boolean;
}) {
  const { user, catalogItem, canOnboard } = props;

  const { status, loading, error, reload } = useCatalogBoardingStatus(
    {
      kind: "Catalog",
      inner: { id: catalogItem.id, name: catalogItem.info.display_name },
    } as Resource,
    user.employee.id
  );

  let element = <></>;

  if (loading) element = <CircularProgress color="secondary" size={32} />;
  else if (error) element = <ErrorChip error={error} />;
  else if (status !== null)
    element = (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        {status.task_id !== undefined && status.task_id !== null && (
          <TaskLoader
            id={status.task_id}
            render={(task) => (
              <PendingAction
                task={task}
                callback={reload}
                onCompleted={reload}
              />
            )}
          />
        )}

        <CatalogBoardingAction
          disabled={Boolean(status.task_id) || (!status.value && !canOnboard)}
          onboarded={status.value}
          catalogItem={catalogItem}
          user={user}
          callback={reload}
        />
      </Box>
    );

  return element;
}
